// Worklist status: what KIND of situation a segment is in.
//
// ---------------------------------------------------------------------------
// Why not the median split
// ---------------------------------------------------------------------------
// `classify` (model.js) bins a row on exactly the two variables already plotted:
// depth below the district median -> critical, else reach above/below median ->
// good/warning. Two consequences, both bad for a worklist:
//
//   1. The pill restates the dot's coordinates in words. A reader who can see
//      the scatter learns nothing from it.
//   2. The split is hard. A unit a hair below the median renders identically to
//      one far below, so severity — the thing an admin actually triages on — is
//      absent from the label.
//
// But severity is already carried, and carried better: sigma-distance from the
// district centroid IS the ranking, and it is continuous. So status is free to
// answer a different question, and the useful one is *what kind of problem is
// this* — because each kind implies a different remedy and a different
// destination. A single "Needs support" verdict collapses "Building" and "Not
// started" into one label despite their being opposite situations, and "Shallow"
// versus "Not started" is a dosage conversation versus an onboarding one.
//
// ---------------------------------------------------------------------------
// Mechanics
// ---------------------------------------------------------------------------
// A rule cascade in priority order, FIRST MATCH WINS. The ordering is the
// load-bearing part, not the specific thresholds:
//
//   Not started -> Under-resourced -> Stalled -> Bursty -> Building -> Shallow
//   -> Thriving
//
// Capacity is checked before anything implying a teaching problem. Never send
// someone to PD when the answer is dark robots.
//
// Any rule whose signal is unavailable is SKIPPED rather than guessed, and a row
// that matches nothing falls back to the median split. So a missing join (no
// `include=volume`, no robot data, a dimension with no capacity story) degrades
// to today's behaviour instead of inventing a label.
//
// Status must not affect ranking: the worklist stays sorted by sigma-distance.
// And the scatter is deliberately untouched — it keeps reading `row.status`, the
// quadrant class, because its channels are already spent (x reach, y depth, size
// roster, colour sigma-distance) and its corner captions name those quadrants.
// The KIND lives on the pill and the sparkline, where words are available.
//
// ---------------------------------------------------------------------------
// Tense
// ---------------------------------------------------------------------------
// Two rules make claims about *now* — Stalled ("none in the last N buckets") and
// the idle half of Bursty. On a completed school year the last buckets are June
// and July, so an ungated version labels essentially every row Stalled. Same trap
// the Overview's fleet card documents at length. So `isCurrent` gates them:
// Stalled is skipped outright on a past year (the retrospective equivalent,
// "stopped before the year ended", is a different finding and not this one), and
// Bursty drops its idle requirement there, since concentration alone already says
// "they pushed once".

import { QUADRANT_TONE } from "./tokens";

// ---------------------------------------------------------------------------
// Tunable thresholds. One object rather than literals scattered through the
// rules, because every one of these is a judgment call.
//
// CALIBRATED against prod, SY25-26 (2025-08-01 .. 2026-07-31), monthly buckets,
// the six highest-volume orgs — 25 school rows across Blue Springs, Rock Hill,
// Bridges Prep, Dillon, Fort Mill and Trinity. Reproduce the distributions quoted
// below with `python manage.py calibrate_adoption_status` in
// comm_management_backend (read-only; --orgs and --start/--end to narrow). Blue Springs is included as a CONTROL, not a
// target: its roster has been frozen since Sept 2025 and much of its fleet is
// long idle, so it reads as pathological on nearly every rule at once — useful
// for checking a rule can fire, useless for placing its threshold.
// ---------------------------------------------------------------------------
const STATUS_CONFIG = {
  // Stalled: this many trailing buckets with no lessons at all. 2 rather than 1
  // because the final bucket of a live window is a PARTIAL one (the window ends
  // today, mid-month), so a single empty tail bucket is routine.
  //
  // NOT validated against a live year — the current school year had barely
  // started when this was calibrated, and on a COMPLETED year the measurement is
  // structurally useless: every district's last two monthly buckets are June and
  // July, and the observed idle tail was exactly 2 for 22 of 25 rows. Which is
  // itself the evidence for gating this rule on `isCurrent`, and the reason it
  // fired on nobody in the calibration run rather than on everybody.
  idleBuckets: 2,

  // Bursty: concentration is the share of a segment's volume landing in its
  // densest contiguous QUARTER of the window, as a ratio to uniform. Evenly
  // spread usage scores 1.0; everything in one quarter scores 4.0. A ratio on
  // purpose: a raw share isn't comparable across bucket grains (2 of 12 monthly
  // buckets is 17% of the window, 2 of 52 weekly ones is 4%), and the ratio form
  // matches how the server's focus-cycle detector states intensity — same idea,
  // same units, one mental model.
  //
  // 3.0, not the 2.0 this started at. Measured distribution over the 23 rows with
  // enough volume to score: min 1.72, p25 1.87, MEDIAN 2.06, p75 2.25, max 4.33.
  // At 2.0 the rule claimed 61% of rows — a status meaning "unusual" that fires on
  // the majority is not a finding, and it sat one hundredth above the median. Note
  // the ~1.7 FLOOR: summer sits inside a school-year window, so ~3 of 12 monthly
  // buckets are empty by construction and perfectly even term usage cannot score
  // 1.0. The scale in practice runs 1.7 -> 4.3.
  //
  // Trimming the empty head and tail to restore a true 1.0 baseline is WRONG — it
  // inverts the ranking. Fort Mill, active in 5 of 13 buckets and the
  // clearest real burst in the sample, drops from 3.06 to 1.38 (the lowest score of
  // all), because a quarter of a short active span is one bucket and one bucket of
  // a five-bucket span is close to uniform by definition. The untrimmed measure is
  // the one that ranks intermittent use above steady use, which is the whole point.
  //
  // At 3.0 the rule claims 2 of 23: Fort Mill (3.06, active 5 of 13 buckets) and
  // Dillon's Lake View (4.33, active 2 of 13). 2.5 would add Voy Spears (2.51) and
  // Lucy Franklin (2.75), both active in 10 of 13 buckets — steady use with a peak
  // month, which is not a push that stopped.
  concentration: 3.0,
  // Volume below which concentration is noise from a handful of lessons. Mirrors
  // the server's CYCLE_MIN_LESSONS. Excluded 2 of 25 rows (a 12-learner high
  // school, a 4-learner site).
  minLessons: 50,

  // Under-resourced: working robots per learner.
  //
  // 1/35, not the obvious 1/60. Measured distribution over the 22 rows with a
  // robot join: min 0.0193, p25 ~0.03, med ~0.06, max 0.109. 1/60 (0.0167) sits
  // BELOW the observed minimum, so a rule at that level can never fire — and the
  // cost is concrete: Dillon's Lake View (313 learners, 8 working robots, 7% reach)
  // falls through to Bursty instead, which sends someone to look at a focus cycle
  // when the answer is that the school has almost no hardware. Precisely the
  // mistake the "capacity before pedagogy" ordering exists to prevent, so the
  // threshold has to be able to fire.
  //
  // 1/35 (0.0286) claims Lake View and leaves Chapel Lakes (0.0296) just outside.
  // Rows with thin hardware but strong usage are unaffected either way — the rule
  // also requires low reach or low depth, which is why Blue Springs' SS James
  // Walker (0.0193 robots/learner, but 67% reach and depth well above median)
  // stays Thriving rather than being blamed on its robot count.
  robotsPerLearner: 1 / 35,

  // Shallow: the share of a segment's roster that reached the recommended dose.
  //
  // Read the calibration before moving this. At-target share is uniformly tiny —
  // 19 of 25 rows under 0.04, median 0.010 — so at 0.10 this clause is true almost
  // everywhere and Shallow is effectively "reach fine, depth low" on its own. It
  // is NOT dead config, though: it binds exactly where it should, on the districts
  // that have actually achieved dosage. Fort Mill reaches 0.398 and Rock Hill
  // 0.093, and calling either "shallow" for sitting below a median would be wrong.
  // So this earns its place as a guard against libelling the best performers, not
  // as a selector. The at-target NUMBER's real value is in the copy the pill and
  // its tooltip carry.
  atTargetShare: 0.1,

  // Building: mean momentum over the last N buckets against the N before them.
  // A unit on its way up should not be flagged, however low it currently sits.
  // Claimed 1 of 25 rows in the calibration, and both halves of the window have to
  // be nonzero, so it is a narrow rule by construction — deliberately, since its
  // job is to SUPPRESS a flag rather than raise one.
  momentumWindow: 2,
  momentumRise: 1.25,
};

// ---------------------------------------------------------------------------
// Vocabulary. One word per status, no per-row prose.
//
// `tone` is the shared verdict scale from tokens.js, so the worklist gains no new
// visual axis — seven kinds, three tones. NONE of them is `action`: every kind
// here is a diagnosis a reader should go LOOK at, and the worklist has no way to
// know whether a stalled school needs a phone call or already had one. Red is
// reserved for the items with a deadline attached (a subscription running out),
// which is a fact about a contract rather than a reading of usage.
//
// The split within "go look" is severity, and it is the same split the cascade
// already makes: the three kinds that mean nothing is happening or nothing CAN
// happen (Not started, Under-resourced, Stalled) earn amber; the two that mean
// something is happening in the wrong shape (Bursty, Shallow) earn blue; the two
// good outcomes are clear.
//
// `help` is the legend text: "Shallow" and "Bursty" are not self-evident, and the
// legend is the ONE place words are added. `lens` names the Adoption lens that
// answers the question, when there is one.
// ---------------------------------------------------------------------------
const KINDS = {
  thriving: {
    label: "Thriving",
    tone: "clear",
    help: "High reach and high mastery per learner.",
  },
  building: {
    label: "Building",
    tone: "clear",
    help:
      "Momentum is rising even though the current numbers are still low — " +
      "on its way up, so not flagged.",
  },
  stalled: {
    label: "Stalled",
    tone: "attention",
    help:
      "Had activity earlier in the year and none recently. A check-in, not a " +
      "curriculum problem.",
  },
  bursty: {
    label: "Bursty",
    tone: "advisory",
    help:
      "Ran a concentrated push rather than steady use. Look at the cycle to " +
      "see whether the intensity paid off.",
    lens: "usage",
  },
  shallow: {
    label: "Shallow",
    tone: "advisory",
    help:
      "Reaching plenty of learners, but few of them get enough lessons to " +
      "matter. A usage conversation.",
    lens: "usage",
  },
  not_started: {
    label: "Not started",
    tone: "attention",
    help: "No activity at all this year — an onboarding problem.",
  },
  under_resourced: {
    label: "Under-resourced",
    tone: "attention",
    help:
      "Low usage AND too few working robots for the roster. Checked before " +
      "anything else, so nobody is sent to PD when the answer is hardware.",
  },
};

// ---------------------------------------------------------------------------
// Signal helpers. Exported because the worklist's pattern micro-bar draws the
// same volume series these read, and the two must not disagree about what
// "concentrated" or "idle" means.
// ---------------------------------------------------------------------------
const sum = (xs) => xs.reduce((a, b) => a + b, 0);

// Share of volume in the densest contiguous quarter of the window, as a ratio to
// uniform. 1.0 == evenly spread, 4.0 == all of it in one quarter. Null when there
// is nothing to measure: no volume field, too few buckets to have a quarter, or
// too little total volume for the ratio to be stable.
const concentration = (volume, cfg = STATUS_CONFIG) => {
  if (!volume || volume.length < 4) return null;
  const total = sum(volume);
  if (total < cfg.minLessons) return null;
  const n = volume.length;
  const span = Math.max(1, Math.round(n / 4));
  let best = 0;
  for (let i = 0; i + span <= n; i++) {
    const s = sum(volume.slice(i, i + span));
    if (s > best) best = s;
  }
  return best / total / (span / n);
};

// How many trailing buckets carried no lessons.
const idleTail = (volume) => {
  if (!volume) return null;
  let k = 0;
  for (let i = volume.length - 1; i >= 0 && !volume[i]; i--) k += 1;
  return k;
};

// Mean of the last `w` entries against the `w` before them. Null when there
// aren't enough buckets, or when the earlier half is zero — a rise from nothing
// is "just started", which Not started / Building-from-zero already covers, and
// dividing by it would report an infinite trend.
const momentumRatio = (values, w) => {
  if (!values || values.length < 2 * w) return null;
  const recent = sum(values.slice(-w)) / w;
  const prior = sum(values.slice(-2 * w, -w)) / w;
  if (prior <= 0) return null;
  return recent / prior;
};

// ---------------------------------------------------------------------------
// The cascade.
//
// `ctx`:
//   medReach, medDepth   the district crosshair (already on every quad row)
//   isCurrent            is the selected school year still running?
//   capacity             optional {working, total} for THIS row, or null when
//                        the dimension has no capacity story (a grade cohort
//                        doesn't own robots) — the Under-resourced rule is
//                        skipped rather than guessed
//   cfg                  threshold overrides
//
// Returns a kind key, or null to mean "no rule applied, use the median split".
// ---------------------------------------------------------------------------
const classifyKind = (row, ctx = {}) => {
  const cfg = { ...STATUS_CONFIG, ...(ctx.cfg || {}) };
  const { medReach = 0, medDepth = 0, isCurrent = true, capacity = null } = ctx;
  const lowReach = row.reach < medReach;
  const lowDepth = row.depth < medDepth;
  const engaged = row.current ? row.current.learnersInWindow : 0;

  // 1. Not started — no learner here engaged at all. Free from data always
  //    present, and it must come first: every rule below would otherwise read a
  //    roster that never touched a robot as some flavour of underperformance.
  if (engaged === 0) return "not_started";

  // 2. Under-resourced — capacity before pedagogy. Requires the robot join, so
  //    it self-skips on the grade and engagement dimensions and whenever robot
  //    utilization failed to load. `capacity` is null for a row robots are not a
  //    property of, and {working:0,total:0} for one that could have them and has
  //    none — see the lookup in DistrictDashboard.
  //
  //    ZERO IS THE STRONGEST CASE, not a missing reading. The guard here used to
  //    require total > 0, back when a zero came out of a Map miss and could not
  //    be told from "no data": a segment with a roster and no robot at all fell
  //    through to a rule implying a teaching problem, which is the exact
  //    mislabel this rule exists to prevent. Now that the lookup distinguishes
  //    the two, roster alone is the guard.
  if (capacity && row.roster > 0) {
    const perLearner = capacity.working / row.roster;
    if (perLearner < cfg.robotsPerLearner && (lowReach || lowDepth))
      return "under_resourced";
  }

  const idle = idleTail(row.volume);
  const conc = concentration(row.volume, cfg);

  // 3. Stalled — was running, isn't now. Present tense, so current year only
  //    (see the Tense note above). `idle < length` keeps a segment that was
  //    never active out of here; rule 1 has usually caught it, but a learner can
  //    have mastery events with no countable lessons in the window.
  if (isCurrent && idle != null && idle >= cfg.idleBuckets && idle < row.volume.length)
    return "stalled";

  // 4. Bursty — concentrated rather than steady. On a live year it also has to
  //    be idle now (otherwise it is a push still in progress, which is not a
  //    finding); on a completed year concentration alone carries it.
  if (conc != null && conc >= cfg.concentration) {
    if (!isCurrent || (idle != null && idle > 0)) return "bursty";
  }

  // 5. Building — do not flag a unit on its way up, whatever its current values.
  //    Momentum is new standards per engaged learner per bucket, which is the
  //    same series the Trend sparkline draws.
  const rise = momentumRatio(row.momentum, cfg.momentumWindow);
  if (rise != null && rise >= cfg.momentumRise && (lowReach || lowDepth))
    return "building";

  // 6. Shallow — reach is fine, depth is not. Stated against the at-target share
  //    when we have it, which is the difference between restating the y-axis and
  //    naming a dosage problem. Without the dosage join it falls back to the
  //    reach/depth reading alone.
  if (!lowReach && lowDepth) {
    if (row.atTargetShare == null || row.atTargetShare < cfg.atTargetShare)
      return "shallow";
  }

  // 7. Thriving — above the crosshair on both axes.
  if (!lowReach && !lowDepth) return "thriving";

  // Nothing matched. In practice this is "low on BOTH axes, steadily" — not
  // starting from zero, not stopped, not concentrated, not on the way up, just
  // consistently behind. Measured on prod it is 36% of school rows, the single
  // largest group, and the vocabulary has no word for it: every kind above names
  // a SPECIAL case, and this is the ordinary one.
  //
  // Left as a fall-through to the median split on purpose rather than given an
  // eighth label. The quadrant classifier's own words for it ("Needs support",
  // "Reaching few") are accurate here — the complaint against that classifier is
  // that it restates the dot's coordinates for EVERY row, which is a problem for
  // the 64% that have a sharper story and not for the rows whose story really is
  // "behind on both". Worth revisiting with a real word if it stays this large.
  return null;
};

// Decorate quad rows with `kind`. Rows keep `status` (the quadrant class) exactly
// as before, so the scatter and the Overview spark are untouched.
//
// `capacityFor(row)` is a lookup, not a field, because the join is per-dimension:
// a school row maps to the robots at that OrgUnit, a grade cohort maps to
// nothing. Pass undefined to skip the rule everywhere.
const withKinds = (rows, { capacityFor, ...ctx } = {}) => {
  const cfg = { ...STATUS_CONFIG, ...(ctx.cfg || {}) };
  return rows.map((row) => {
    const kind = classifyKind(row, {
      ...ctx,
      medReach: row.medReach,
      medDepth: row.medDepth,
      capacity: capacityFor ? capacityFor(row) : null,
    });
    return {
      ...row,
      kind,
      // The pill's tone. Falls back to the quadrant class's tone so a row no rule
      // claimed is toned on the same scale as one that matched.
      kindTone: kind ? KINDS[kind].tone : QUADRANT_TONE[row.status] || null,
      kindLabel: kind ? KINDS[kind].label : null,
      // Carried for the pattern micro-bar's own copy, so the bar and the status
      // are reading one number rather than two computations of it.
      concentration: concentration(row.volume, cfg),
      idleBuckets: idleTail(row.volume),
    };
  });
};

// The tone ONE row wears, wherever it is drawn — its worklist pill, its trend
// sparkline, its dot and comet trail on the scatter, its dot in the Overview's
// spark. All of those are the same row, so all of them read this.
//
// It is one function because per-caller colour choices desynchronize: colour the
// scatter dots by `status` (the quadrant) while the worklist pill reads the kind
// and a Bursty school wears two colours on one screen; change the pill's tones
// without the plot marks and a school labelled amber draws a red dot with a red
// comet trail beside it. A reader has no way to know those are the same
// statement. So: one function, and no caller picks a colour for a row by itself.
//
// Rows that never went through `withKinds` (the cohort drill-downs, which have a
// quadrant class and nothing else) fall back to the quadrant tone, so this is
// safe to call on any row that carries either field.
const rowTone = (row) =>
  (row && row.kindTone) || (row && QUADRANT_TONE[row.status]) || null;

// Which kinds actually occur in a set of rows — the legend renders only these, so
// a district with no capacity join never sees an "Under-resourced" entry it can't
// produce. Ordered by the cascade so the legend reads in priority order.
const CASCADE_ORDER = [
  "not_started",
  "under_resourced",
  "stalled",
  "bursty",
  "building",
  "shallow",
  "thriving",
];
const kindsPresent = (rows) => {
  const seen = new Set(rows.map((r) => r.kind).filter(Boolean));
  return CASCADE_ORDER.filter((k) => seen.has(k));
};

// The quadrant classes worn by rows NO rule claimed, so the legend can cover them
// too. On prod these are 36% of school rows — the largest single group — and a
// legend that explained only the seven kinds would leave a third of the pills on
// screen undocumented, which is worse than the median split this stands in for.
const FALLBACK_HELP = {
  critical: "Behind on both reach and mastery, steadily — no sharper pattern.",
  warning: "Reaching fewer learners than most, with mastery around the median.",
  good: "Around or above the district median on both.",
};
const fallbacksPresent = (rows) => {
  const seen = new Set(
    rows.filter((r) => !r.kind).map((r) => r.status).filter(Boolean)
  );
  return ["critical", "warning", "good"].filter((s) => seen.has(s));
};

export {
  KINDS,
  rowTone,
  CASCADE_ORDER,
  STATUS_CONFIG,
  classifyKind,
  withKinds,
  kindsPresent,
  fallbacksPresent,
  FALLBACK_HELP,
  concentration,
  idleTail,
  momentumRatio,
};
